import { useEffect } from 'react';

// hadaka.jp が使っている Lenis 相当の慣性スクロールを、依存なしで実装したもの。
// タッチ端末と prefers-reduced-motion ではネイティブのスクロールに任せる。
export default function useSmoothScroll({ lerp = 0.1 } = {}) {
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (reduceMotion || coarsePointer) return undefined;

    const root = document.documentElement;
    root.classList.add('has-smooth-scroll');

    let target = window.scrollY;
    let current = target;
    let rafId = null;

    const maxScroll = () =>
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const clamp = (v) => Math.min(Math.max(v, 0), maxScroll());

    const tick = () => {
      const diff = target - current;

      if (Math.abs(diff) < 0.3) {
        current = target;
        rafId = null;
        window.scrollTo(0, current);
        return;
      }

      current += diff * lerp;
      window.scrollTo(0, current);
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (rafId === null) rafId = requestAnimationFrame(tick);
    };

    const onWheel = (e) => {
      if (e.ctrlKey) return; // ピンチズームは邪魔しない
      e.preventDefault();
      target = clamp(target + e.deltaY);
      start();
    };

    // スクロールバー操作やキーボードなど、自前のアニメ以外で動いた分を取り込む
    const onScroll = () => {
      if (rafId === null) {
        target = window.scrollY;
        current = target;
      }
    };

    // アンカーリンクも同じ慣性で移動させる
    const onClick = (e) => {
      const link = e.target.closest && e.target.closest('a[href^="#"]');
      if (!link) return;

      const id = link.getAttribute('href').slice(1);
      if (!id) return;

      const el = document.getElementById(id);
      if (!el) return;

      e.preventDefault();
      target = clamp(el.getBoundingClientRect().top + window.scrollY);
      start();
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onClick);
      if (rafId !== null) cancelAnimationFrame(rafId);
      root.classList.remove('has-smooth-scroll');
    };
  }, [lerp]);
}
